import { TruncationWithTooltip } from "@/components/text/TruncationWithTooltip";
import { SelectableCard } from "@/components/ui/SelectableCard";
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Expert } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faXmark,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { ExpertAvatar } from "../ExpertAvatar";
import { trimMax } from "./lib";

export type ExpertItemsProps = {
  groupUuid: string | null;
  experts: Expert[];
  selectedExpertUuids: Set<string> | null;
  previousSelectedExpertUuids: Set<string> | null;
  onExpertSelection: (
    selectedExpertUuids: Set<string>,
    unselectedExpertUuids: Set<string>,
  ) => void;
  className?: string;
  max: number | null;
  disabled?: boolean;
  disableUnselect?: boolean;
  variant: "list" | "cards";
  onExpertElementClick?: (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLLIElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
};

export const ExpertItems = ({
  groupUuid,
  max,
  selectedExpertUuids: initialSelectedExpertUuids,
  previousSelectedExpertUuids,
  experts,
  onExpertSelection,
  disabled = false,
  disableUnselect,
  variant,
  onExpertElementClick,
  className,
}: ExpertItemsProps) => {
  const [selectedExpertUuids, setSelectedExpertUuids] = useState<Set<string>>(
    new Set(),
  );
  const [unselectedExpertUuids, setUnselectedExpertUuids] = useState<
    Set<string>
  >(new Set());
  const hasReachedMax = max === null ? false : selectedExpertUuids.size >= max;

  useEffect(() => {
    setSelectedExpertUuids(new Set(initialSelectedExpertUuids ?? []));
  }, [initialSelectedExpertUuids]);

  const handleExpertClick = (
    expert: Expert,
    event:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLLIElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    if (disabled) {
      return;
    }
    const wasOriginallySelected = previousSelectedExpertUuids?.has(expert.uuid);
    const isSelected = selectedExpertUuids.has(expert.uuid);
    const isUnselected = unselectedExpertUuids.has(expert.uuid);

    const updatedSelected = new Set(selectedExpertUuids);
    const updatedUnselected = new Set(unselectedExpertUuids);

    // Original selected can only be unselected
    if (wasOriginallySelected) {
      if (isUnselected) {
        updatedUnselected.delete(expert.uuid);
      } else {
        updatedUnselected.add(expert.uuid);
      }
    }
    // Others can only be selected
    else {
      if (isSelected) {
        if (!disableUnselect) updatedSelected.delete(expert.uuid);
      } else {
        updatedSelected.add(expert.uuid);
      }
    }

    const trimmedSelected =
      max === null ? updatedSelected : trimMax(updatedSelected, max);
    setSelectedExpertUuids(trimmedSelected);
    setUnselectedExpertUuids(updatedUnselected);
    onExpertSelection(trimmedSelected, updatedUnselected);
    onExpertElementClick?.(event);
  };

  return (
    <AnimatePresence initial={false}>
      {experts.map((expert) => {
        const isUnselected = unselectedExpertUuids.has(expert.uuid);
        const isPreviousSelected = previousSelectedExpertUuids?.has(
          expert.uuid,
        );
        const isSelected = selectedExpertUuids.has(expert.uuid);
        const isDisabled =
          disabled || (max !== null && max > 1 && !isSelected && hasReachedMax);
        const isEffectivelySelected =
          isSelected || (isPreviousSelected && !isUnselected);

        const handleClick = (
          e:
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.MouseEvent<HTMLLIElement, MouseEvent>
            | React.MouseEvent<HTMLButtonElement, MouseEvent>,
        ) => {
          e.preventDefault();
          handleExpertClick(expert, e);
        };

        const expertContent = (
          <>
            {max !== 1 && (
              <Tooltip>
                <TooltipContent>
                  {!isSelected &&
                    !isPreviousSelected &&
                    !isUnselected &&
                    "Add this expert"}
                  {isSelected && "This expert will be added"}
                  {isPreviousSelected &&
                    !isUnselected &&
                    "This expert was already added"}
                  {isUnselected && "This expert will be removed"}
                </TooltipContent>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      "text-foreground",
                      "border-foreground/20 flex h-6 w-6 items-center justify-center rounded-md border-1",
                      {
                        "m-2": variant === "list",
                        "absolute top-2 left-2": variant === "cards",
                        "text-foreground/20":
                          isPreviousSelected && !isUnselected,
                      },
                    )}
                  >
                    {(isSelected || isPreviousSelected || isUnselected) && (
                      <FontAwesomeIcon
                        icon={isUnselected ? faXmark : faCheck}
                      />
                    )}
                  </div>
                </TooltipTrigger>
              </Tooltip>
            )}
            <ExpertAvatar
              expert={expert}
              size={variant === "list" ? "lg" : "xl"}
            />
            <div
              className={cn("flex flex-1 flex-col", {
                "items-start gap-1": variant === "list",
                "items-center gap-2": variant === "cards",
              })}
            >
              <TruncationWithTooltip
                className={cn("text-sm font-semibold", {
                  "line-clamp-2 text-center": variant === "cards",
                })}
              >
                {expert.name}
              </TruncationWithTooltip>
              {expert.group && expert.group.uuid !== groupUuid && (
                <TruncationWithTooltip
                  className={cn("text-accent truncate text-xs font-bold", {
                    "text-center": variant === "cards",
                  })}
                >
                  by {expert.group.name}
                </TruncationWithTooltip>
              )}
              <TruncationWithTooltip
                className={cn("text-foreground-8 text-xs", {
                  "line-clamp-2": variant === "list",
                  "line-clamp-3 text-center": variant === "cards",
                })}
              >
                {expert.description}
              </TruncationWithTooltip>
            </div>
          </>
        );

        if (variant === "cards") {
          return (
            <SelectableCard
              key={expert.uuid}
              selected={isEffectivelySelected}
              disabled={isDisabled}
              className={className}
              onClick={handleClick}
            >
              {expertContent}
            </SelectableCard>
          );
        }

        const ItemComponent =
          max === 1
            ? PresenceDropdownMenuItem
            : PresenceDropdownMenuCheckboxItem;

        return (
          <ItemComponent
            key={expert.uuid}
            disabled={isDisabled}
            className={cn(
              "relative flex items-center",
              "gap-4 p-4",
              "border-foreground-2 rounded-2xl border-1 border-2 bg-white",
              "transition-[translate_shadow] duration-150",
              "flex-row",
              {
                "cursor-pointer": !isDisabled,
                "bg-background-3": isEffectivelySelected,
                "pl-2": max !== 1,
              },
              className,
            )}
            onClick={handleClick}
          >
            {expertContent}
          </ItemComponent>
        );
      })}
    </AnimatePresence>
  );
};

const listPresenceProps = {
  initial: {
    height: 0,
    scaleY: 0,
    opacity: 0,
  },
  animate: {
    height: "auto",
    scaleY: 1,
    opacity: 1,
  },
  exit: { height: 0, scaleY: 0, opacity: 0 },
  transition: { duration: 0.2, ease: "easeInOut" as const },
  style: {
    overflow: "hidden",
    transformOrigin: "top",
  },
};

const PresenceDropdownMenuItem: React.FC<
  React.ComponentProps<typeof DropdownMenuItem>
> = ({ children, ...rest }) => {
  return (
    <motion.div {...listPresenceProps}>
      <DropdownMenuItem {...rest}>{children}</DropdownMenuItem>
    </motion.div>
  );
};

const PresenceDropdownMenuCheckboxItem: React.FC<
  React.ComponentProps<typeof DropdownMenuCheckboxItem>
> = ({ children, ...rest }) => {
  return (
    <motion.div {...listPresenceProps}>
      <DropdownMenuCheckboxItem {...rest}>{children}</DropdownMenuCheckboxItem>
    </motion.div>
  );
};
